import React, { useEffect, useRef } from 'react';
import 'theoplayer/ui.css';
import 'styles/scss/components/theoPlayer.scss';
import logger from 'utils/loggerUtils';
import { monitorTheoEvents } from './playerEvents';

const postMessageToParent = (data, origin) => {
  const parentWindow = window.parent;
  if (parentWindow) {
    parentWindow.postMessage(data, origin);
  }
};

export default function TheoPlayer(props) {
  const { options, playerStateCheckHandler, playerErrorHandler, playerStalledHandler } = props;
  const _el = useRef(null);
  const playerRef = useRef(null);
  const sourceRef = useRef(options.src);

  useEffect(() => {
    if (playerRef.current && options.src && options.src !== sourceRef.current) {
      let source = {};
      if (options.srcType === 'hls') {
        source = {
          sources: [
            {
              src: options.src,
              type: 'application/x-mpegurl',
            },
          ],
        };
      } else {
        source = {
          sources: [
            {
              src: options.src,
              type: 'application/dash+xml',
            },
          ],
        };
      }
      playerRef.current.source = source;
    }
    sourceRef.current = options.src;
  }, [options.src, options.srcType]);

  useEffect(() => {
    logger.log('theo mount');
    let monitor = null;
    let previousProgress = 0;
    const errorHandler = () => {
      playerErrorHandler();
      logger.info('error handle');
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (monitor !== null) {
        clearInterval(monitor);
      }
    };

    if (_el.current) {
      import(/* webpackChunkName: "theoplayer" */ 'theoplayer').then((THEOplayer) => {
        playerRef.current = new THEOplayer.Player(_el.current, {
          libraryLocation: `/static/js/theoplayer4-10-0`,
          // TODO Uncomment the line below, and set its value to your THEOplayer license
          license: `${process.env.REACT_APP_THEO_PLAYER_LICENSE}`,
          mutedAutoplay: 'all',
        });
        playerRef.current.autoplay = true;

        let source = null;
        if (options.srcType === 'hls') {
          let src = {
            src: sourceRef.current,
            type: 'application/x-mpegurl',
          };

          if (options.isDrm) {
            let drmConfiguration = {
              integration: 'axinom',
              token: options.drmConfig.drmToken,
              fairplay: {
                licenseAcquisitionURL: options.drmConfig.fairPlayLicenseAcquisitionUrl,
                certificateURL: options.drmConfig.fairPlayCertificateUrl,
              },
            };
            src.contentProtection = drmConfiguration;
          }

          source = {
            sources: src,
          };
        } else {
          let src = {
            src: sourceRef.current,
            type: 'application/dash+xml',
          };

          if (options.isDrm) {
            let drmConfiguration = {
              integration: 'axinom',
              token: options.drmConfig.drmToken,
              playready: {
                licenseAcquisitionURL: options.drmConfig.playreadyLicenseAcquisitionUrl,
              },
              widevine: {
                licenseAcquisitionURL: options.drmConfig.widevineLicenseAcquisitionUrl,
              },
            };
            src.contentProtection = drmConfiguration;
          }

          source = {
            sources: src,
          };
        }

        monitorTheoEvents(playerRef.current, THEOplayer.version, Date.now());

        playerRef.current.source = source;
        playerRef.current.addEventListener('play', () => {
          logger.log('play');
        });

        playerRef.current.addEventListener('error', function (event) {
          logger.error(event);
          errorHandler();
        });

        playerRef.current.addEventListener('ended', function (event) {
          logger.log('ended');
          if (!options.isVod) {
            errorHandler();
          }
        });

        if (monitor !== null) {
          clearInterval(monitor);
        }

        monitor = setInterval(() => {
          let currentProgress = 0;
          if (playerRef.current) {
            currentProgress = playerRef.current.currentTime;
            if (previousProgress !== currentProgress) {
              if (currentProgress !== 0 && previousProgress === 0) {
                // determine player started by time updated
                // this.$emit('instanceStarted')
              }
              playerStateCheckHandler({
                state: 'instancePlaying',
                previous: previousProgress,
                current: currentProgress,
              });
            } else {
              if (playerRef.current.paused) {
                playerStateCheckHandler({
                  state: 'instancePaused',
                  previous: previousProgress,
                  current: currentProgress,
                });
              } else {
                playerStalledHandler({ previous: previousProgress, current: currentProgress });
              }
            }
            previousProgress = currentProgress;
          } else {
            playerStalledHandler({ previous: previousProgress, current: currentProgress });
          }
        }, options.checkingInterval);
      });
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      logger.log('theo unmount');
      if (monitor !== null) {
        clearInterval(monitor);
      }
    };
  }, [
    options.checkingInterval,
    options.drmConfig.drmToken,
    options.drmConfig.fairPlayCertificateUrl,
    options.drmConfig.fairPlayLicenseAcquisitionUrl,
    options.drmConfig.playreadyLicenseAcquisitionUrl,
    options.drmConfig.widevineLicenseAcquisitionUrl,
    options.isDrm,
    options.isVod,
    options.srcType,
    playerErrorHandler,
    playerStalledHandler,
    playerStateCheckHandler,
  ]);

  useEffect(() => {
    const postMessageHandler = (e) => {
      if (e.data && e.data.action) {
        const action = e.data.action;
        switch (action) {
          case 'SEEK':
            if (playerRef.current) {
              if (options.isVod && !isNaN(e.data.value)) {
                const targetTime = parseInt(e.data.value);
                if (playerRef.current.duration > targetTime) {
                  playerRef.current.currentTime = targetTime;
                }
              }
            }
            break;
          case 'GET_CURRENT_POSITION':
            if (playerRef.current) {
              if (options.isVod) {
                const currentTime = playerRef.current.currentTime;
                postMessageToParent(
                  {
                    value: currentTime,
                  },
                  e.origin === 'null' ? '*' : e.origin
                );
              }
            }
            break;
          case 'GET_PROGRAM_TIME':
            if (playerRef.current) {
              if (!options.isVod) {
                const currentTime = playerRef.current.currentProgramDateTime;
                postMessageToParent(
                  {
                    value: currentTime ? currentTime.toISOString() : null,
                  },
                  e.origin === 'null' ? '*' : e.origin
                );
              }
            }
            break;
          default:
            break;
        }
      }
    };

    if (options.isEmbed) {
      window.addEventListener('message', postMessageHandler, false);
    }

    return () => {
      window.removeEventListener('message', postMessageHandler);
    };
  }, [options.isEmbed, options.isVod]);

  return (
    <div
      // vjs-16-9 THEOplayer are not necessary, but just added for demo purposes
      className={'theoplayer-container video-js theoplayer-skin vjs-16-9 THEOplayer'}
      // The ref prop here is key it returns the actual dom element and not the virtual react dom elements
      // Which is need by the player
      ref={_el}
    />
  );
}
